import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { absoluteUrl } from '@/shared/lib/internal-links';
import {
  Button,
  Combobox,
  type ComboboxOption,
  Dialog,
  DialogActions,
  type MarkdownLinkPickerProps,
  TextField,
} from '@/shared/ui';

import { type Destination } from './destinations';
import { useDestinations } from './use-destinations';

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

/**
 * The link chooser of every Markdown editor (task and card text, board description, ...): pick a
 * Planosfera item from the same list as "Go to..." - its address carries the item's id, so it
 * survives renames and moves - or type an ordinary external address. The result is a plain
 * Markdown link, so nothing about the item's name or access is stored in the text beyond the
 * label the author chose.
 */
export function MarkdownLinkDialog({ selectedText, onInsert, onClose }: MarkdownLinkPickerProps) {
  const { t } = useTranslation();
  const { destinations } = useDestinations();
  const [picked, setPicked] = useState<Destination | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState(selectedText);
  const [error, setError] = useState(false);
  const options: ComboboxOption[] = destinations.map((destination) => ({
    value: destination.key,
    label: destination.name,
    description: [t(`quickNav.kinds.${destination.kind}`), destination.context]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <Dialog title={t('links.insertTitle')} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation(); // the form lives inside the editor's own form (a task dialog)
          const raw = url.trim();
          if (raw === '') {
            setError(true);
            return;
          }
          const href = picked ? raw : /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
          if (!SAFE_URL.test(href)) {
            setError(true);
            return;
          }
          onInsert(text.trim() || picked?.name || href, href);
        }}
      >
        <Combobox
          label={t('links.toResource')}
          value={picked?.key ?? null}
          options={options}
          placeholder={t('links.pick')}
          searchPlaceholder={t('common.search')}
          emptyText={t('common.noResults')}
          onChange={(key) => {
            const destination = destinations.find((item) => item.key === key) ?? null;
            setPicked(destination);
            if (destination) {
              setUrl(absoluteUrl(destination.to));
              if (text.trim() === '') setText(destination.name);
              setError(false);
            }
          }}
        />
        <TextField
          label={t('links.text')}
          value={text}
          maxLength={200}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        <TextField
          label={t('documents.editor.linkUrl')}
          value={url}
          inputMode="url"
          error={error ? t('documents.editor.linkInvalid') : undefined}
          data-testid="md-link-url"
          onChange={(event) => {
            setUrl(event.target.value);
            setPicked(null);
            setError(false);
          }}
        />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary">
            {t('links.insert')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
